import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';

const SHORT_VIEWPORT_PX = 560;

/**
 * Видимая часть экрана на телефоне. Экранная клавиатура уменьшает только visual viewport:
 * 100vh и position: fixed по-прежнему считают от всего экрана, поэтому модалка остается
 * по центру под клавиатурой. Высоту и сдвиг видимой области пишем в --z-vvh / --z-vvt
 * на <html>, а styles.scss держит по ним модалки над клавиатурой.
 */
@Injectable({ providedIn: 'root' })
export class VisualViewportService {
  private readonly document = inject(DOCUMENT);

  constructor() {
    const viewport = this.document.defaultView?.visualViewport;
    if (!viewport) return;

    const onScroll = () => this.writeVars(viewport);
    const onResize = () => {
      this.writeVars(viewport);
      this.revealFocusedField(viewport);
    };
    viewport.addEventListener('resize', onResize);
    viewport.addEventListener('scroll', onScroll);
    this.writeVars(viewport);

    inject(DestroyRef).onDestroy(() => {
      viewport.removeEventListener('resize', onResize);
      viewport.removeEventListener('scroll', onScroll);
    });
  }

  private writeVars(viewport: VisualViewport): void {
    const root = this.document.documentElement;
    root.style.setProperty('--z-vvh', `${viewport.height}px`);
    root.style.setProperty('--z-vvt', `${viewport.offsetTop}px`);
    // Низкая видимая область (открыта клавиатура, телефон боком) — модалки ужимают шапку и кнопки
    root.classList.toggle('z-viewport-short', viewport.height < SHORT_VIEWPORT_PX);
  }

  /** Клавиатура открылась — возвращаем поле в фокусе в видимую часть модалки */
  private revealFocusedField(viewport: VisualViewport): void {
    if (viewport.height >= this.document.documentElement.clientHeight) return;
    const active = this.document.activeElement;
    if (active instanceof HTMLElement && active.closest('.mat-mdc-dialog-container')) {
      requestAnimationFrame(() => active.scrollIntoView({ block: 'nearest' }));
    }
  }
}
